// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Brim — the PURE game core. One mechanic, two ways to lose:
//   · the CHARGE (pot) swells while you hold an orb, accelerating;
//   · each orb has a hidden BRIM (an overflow time). Hold past it → OVERFLOW (greed).
//   · a jolt tightens your flinch threshold; if measured MOTION beats it → SPILL (flinch).
//   · RELEASE before either to bank the charge into your score.
// The sensor activates the second loss (motion energy). With no sensor motion stays 0,
// so only overflow can bite — the desktop/fallback "timing gamble", a real game.
//
// The core is deterministic and clock-free: it advances only via step(motionEnergy, dt)
// and release(), which is what lets the sim (tests/balance) and the mechanism audit
// (tests/mechanism) drive the EXACT shipped logic without a browser or a sensor. Every
// orb is a pure function of (mode, seed); a live race needs nothing on the wire.

import { makeRng, randFloat, randInt, type Rng } from '@ben-gy/game-engine/rng';
import {
  AGITATION_NOISE,
  FEVER_REF,
  FEVER_TIGHTEN,
  GRACE_MS,
  POT_A,
  POT_B,
  STARTLE_MS,
  STARTLE_TIGHTEN,
  type Mode,
} from './modes';

export type JoltKind = 'flash' | 'buzz' | 'feint' | 'lunge';
export type BustKind = 'overflow' | 'spill';

const TRANSITION_MS = 720; // the beat between orbs (a banked/spilled flash)
const MULT_CAP_BANKS = 10;

export interface Jolt {
  /** Fraction of the orb's brim at which this jolt fires (clustered toward the brim). */
  readonly atFrac: number;
  /** Startle intensity 0.3..~1.2. */
  readonly k: number;
  readonly kind: JoltKind;
}

export interface Orb {
  /** The hidden overflow time in ms. Overflow when potMs ≥ brimMs. */
  readonly brimMs: number;
  readonly jolts: Jolt[];
  /** Seeds the deterministic ± noise on the visible agitation tell. */
  readonly noiseSeed: number;
}

export interface OrbResult {
  index: number;
  banked: number; // charge banked (0 on a bust), already × multiplier
  pot: number; // the raw charge at the moment it ended
  potCeil: number; // the charge at the brim — the most this orb could ever have held
  bust: boolean;
  bustKind: BustKind | null;
  mult: number; // the multiplier in force for this orb (1 outside Streak)
}

export type GameEvent =
  | { t: 'jolt'; kind: JoltKind; k: number }
  | { t: 'bank'; amount: number; orb: number; pot: number }
  | { t: 'bust'; kind: BustKind; orb: number; pot: number }
  | { t: 'orb'; index: number }
  | { t: 'over' };

export interface GameState {
  readonly mode: Mode;
  readonly seed: number;
  readonly orbs: Orb[];
  orbIndex: number;
  holding: boolean; // is the current orb swelling (vs a transition beat / over)
  over: boolean;
  potMs: number; // ms into the current hold
  pot: number; // current charge value
  score: number; // banked total
  banks: number; // successful releases so far (drives the Streak multiplier)
  multiplier: number; // multiplier in force for the current orb
  clock: number; // monotonic play clock (advances only in step)
  transitionUntil: number;
  graceUntil: number;
  startleUntil: number;
  startleK: number;
  nextJolt: number;
  agitation: number; // 0..~1.15 — the visible tell (≈ potMs/brimMs + seeded noise)
  threshold: number; // the current jolt/fever-adjusted flinch threshold
  lastBust: BustKind | null;
  pendingOver: boolean; // Streak: a bust ends the run after the transition beat
  results: OrbResult[];
  events: GameEvent[];
}

/** The charge held after `ms` of holding: pot(s) = POT_A·s + POT_B·s²  (accelerating). */
export function potAt(ms: number): number {
  const s = Math.max(0, ms) / 1000;
  return POT_A * s + POT_B * s * s;
}

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

function hash01(a: number, b: number): number {
  let h = (a ^ Math.imul(b, 0x9e3779b1)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x21f0aaad) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x735a2d97) >>> 0;
  h = (h ^ (h >>> 15)) >>> 0;
  return h / 4294967296;
}

const JOLT_KINDS: JoltKind[] = ['flash', 'buzz', 'lunge', 'feint'];

function makeOrb(mode: Mode, rng: Rng): Orb {
  const brimMs = randFloat(rng, mode.brimMinMs, mode.brimMaxMs);
  const count = randInt(rng, mode.joltMin, mode.joltMax);
  const jolts: Jolt[] = [];
  for (let i = 0; i < count; i++) {
    // Cluster the jolts toward the brim (a rising power curve) so their density telegraphs it.
    const base = (i + 1) / (count + 1);
    const frac = clamp(0.26 + (0.97 - 0.26) * Math.pow(base, 1.35) + randFloat(rng, -0.03, 0.03), 0.2, 0.98);
    const k = clamp(mode.joltIntensity * (0.45 + 0.55 * frac) + randFloat(rng, -0.08, 0.08), 0.3, 1.25);
    jolts.push({ atFrac: frac, k, kind: JOLT_KINDS[randInt(rng, 0, JOLT_KINDS.length - 1)] });
  }
  jolts.sort((a, b) => a.atFrac - b.atFrac);
  return { brimMs, jolts, noiseSeed: randInt(rng, 1, 0x7fffffff) };
}

export function createGame(mode: Mode, seed: number): GameState {
  const rng = makeRng(seed);
  const orbs: Orb[] = [];
  for (let i = 0; i < mode.orbs; i++) orbs.push(makeOrb(mode, rng));

  const s: GameState = {
    mode,
    seed: seed >>> 0,
    orbs,
    orbIndex: 0,
    holding: false,
    over: false,
    potMs: 0,
    pot: 0,
    score: 0,
    banks: 0,
    multiplier: 1,
    clock: 0,
    transitionUntil: 0,
    graceUntil: 0,
    startleUntil: 0,
    startleK: 0,
    nextJolt: 0,
    agitation: 0,
    threshold: mode.tau0,
    lastBust: null,
    pendingOver: false,
    results: [],
    events: [],
  };
  startOrb(s);
  return s;
}

export function currentOrb(s: GameState): Orb {
  return s.orbs[Math.min(s.orbIndex, s.orbs.length - 1)];
}

function startOrb(s: GameState): void {
  s.potMs = 0;
  s.pot = 0;
  s.nextJolt = 0;
  s.startleUntil = 0;
  s.startleK = 0;
  s.agitation = 0;
  s.multiplier = 1 + s.mode.multStep * Math.min(s.banks, MULT_CAP_BANKS);
  s.graceUntil = s.clock + GRACE_MS;
  s.threshold = thresholdOf(s);
  s.holding = true;
  s.events.push({ t: 'orb', index: s.orbIndex });
}

/** The current flinch threshold: base, tightened by Fever (charge) and any active startle. */
export function thresholdOf(s: GameState): number {
  let tau = s.mode.tau0;
  if (s.mode.fever) tau *= 1 - FEVER_TIGHTEN * clamp(s.pot / FEVER_REF, 0, 1);
  if (s.clock < s.startleUntil) tau *= 1 - STARTLE_TIGHTEN * s.startleK;
  return tau;
}

function endOrb(s: GameState, banked: number, kind: BustKind | null): void {
  const orb = currentOrb(s);
  s.holding = false;
  s.transitionUntil = s.clock + TRANSITION_MS;
  s.results.push({
    index: s.orbIndex,
    banked,
    pot: s.pot,
    potCeil: potAt(orb.brimMs),
    bust: kind !== null,
    bustKind: kind,
    mult: s.multiplier,
  });
  if (kind) {
    s.lastBust = kind;
    s.events.push({ t: 'bust', kind, orb: s.orbIndex, pot: s.pot });
    if (s.mode.oneLife) s.pendingOver = true;
  } else {
    s.events.push({ t: 'bank', amount: banked, orb: s.orbIndex, pot: s.pot });
  }
}

/**
 * Advance the current orb by `dtMs` under the latest measured `motionEnergy` (0 when
 * there is no motion sensor — the timing-gamble fallback). Returns nothing; read state
 * and drainEvents() after. Safe to call after `over` (no-op).
 */
export function step(s: GameState, motionEnergy: number, dtMs: number): void {
  if (s.over || dtMs <= 0) return;
  s.clock += dtMs;

  if (!s.holding) {
    // Between orbs: a short beat, then the next orb (or the end of the run).
    if (s.clock >= s.transitionUntil) {
      if (s.pendingOver) {
        s.over = true;
        s.events.push({ t: 'over' });
        return;
      }
      s.orbIndex++;
      if (s.orbIndex >= s.orbs.length) {
        s.over = true;
        s.events.push({ t: 'over' });
        return;
      }
      startOrb(s);
    }
    return;
  }

  const orb = currentOrb(s);
  s.potMs += dtMs;
  s.pot = potAt(s.potMs);
  const frac = s.potMs / orb.brimMs;

  while (s.nextJolt < orb.jolts.length && frac >= orb.jolts[s.nextJolt].atFrac) {
    const j = orb.jolts[s.nextJolt++];
    s.startleUntil = s.clock + STARTLE_MS;
    s.startleK = j.k;
    s.events.push({ t: 'jolt', kind: j.kind, k: j.k });
  }

  const bucket = Math.floor(s.potMs / 120);
  const noise = (hash01(orb.noiseSeed, bucket) - 0.5) * 2 * AGITATION_NOISE;
  s.agitation = clamp(frac + noise, 0, 1.15);
  s.threshold = thresholdOf(s);

  // Overflow (greed) is the hard ceiling — holding past the brim always ends the orb.
  if (s.potMs >= orb.brimMs) {
    endOrb(s, 0, 'overflow');
    return;
  }
  // Spill (flinch) — only after the settle grace, and only if motion beat the threshold.
  if (s.clock >= s.graceUntil && motionEnergy > s.threshold) {
    endOrb(s, 0, 'spill');
  }
}

/** Release the current orb: bank score += pot × multiplier. Returns the amount banked. */
export function release(s: GameState): number {
  if (s.over || !s.holding) return 0;
  const gained = Math.round(s.pot * s.multiplier);
  s.score += gained;
  s.banks++;
  endOrb(s, gained, null);
  return gained;
}

/** Take and clear the queued juice/audit events. */
export function drainEvents(s: GameState): GameEvent[] {
  if (s.events.length === 0) return [];
  return s.events.splice(0);
}
